import { Autocomplete, Popper, TextField } from '@mui/material'
import { FieldContainer } from '../../functional'
import MiniRoleCard, { MiniRoleCardProps } from './MiniRoleCard'
import SVG from '../../../assets/svg'
import styled from '@emotion/styled'
import { useCallback, useRef } from 'react'
import { border } from '../../../styles'

interface Props {
   name: string
   roles: MiniRoleCardProps[]
   label: string
}

interface SelectedRoleCardProps {
   id: string
   onClose: () => void
}

const AutoCompleteSelectField: React.FC<Props> = ({ name, roles, label }: Props) => {
   const fieldContainerRef = useRef(null)
   const SelectedRoleCard: React.FC<SelectedRoleCardProps> = ({ id, onClose }) => {
      return (
         <div className="mini-selected-role">
            <MiniRoleCard {...(roles.find((role) => role.id === id) as MiniRoleCardProps)} />
            <SVG.form.close className="close-button" onClick={onClose} />
         </div>
      )
   }
   const PopperComponent = useCallback((props: any) => {
      return (
         <Popper
            {...props}
            anchorEl={fieldContainerRef.current}
            placement="bottom"
            container={fieldContainerRef.current}
         />
      )
   }, [])
   return (
      <Holder ref={fieldContainerRef}>
         <FieldContainer name={name}>
            {(fieldProps) => {
               const hasValue: boolean = !!fieldProps.value
               const selectedRoleProps = {
                  id: fieldProps.value,
                  onClose: () => fieldProps.onChange({ target: { name, value: '' } }),
               }
               return (
                  <>
                     {!hasValue && (
                        <Autocomplete
                           options={roles}
                           renderInput={(params) => (
                              <TextField
                                 {...params}
                                 placeholder="Search for a role"
                                 label={label}
                                 name={name}
                                 value={fieldProps.value}
                                 helperText={fieldProps.error && fieldProps.helperText}
                                 error={fieldProps.error}
                              />
                           )}
                           value={fieldProps.value}
                           onChange={(_, value) =>
                              fieldProps.onChange({
                                 target: { name, value: value?.id },
                              })
                           }
                           onBlur={fieldProps.onBlur}
                           renderOption={(props, option: MiniRoleCardProps) => (
                              <li {...props} key={option.id}>
                                 <MiniRoleCard {...option} />
                              </li>
                           )}
                           getOptionLabel={(option) =>
                              option.displayName +
                                 option.companyName +
                                 option.role +
                                 option.email || ''
                           }
                           PopperComponent={PopperComponent}
                        />
                     )}
                     {hasValue && <SelectedRoleCard {...selectedRoleProps} />}
                  </>
               )
            }}
         </FieldContainer>
      </Holder>
   )
}

const Holder = styled.div`
   position: relative;
   width: 100%;
   box-sizing: border-box;
   height: 5em;
   .mini-selected-role {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.3em 0.8em;
      padding-bottom: 0em;
      background-color: #f0f0f0;
      border-radius: 0.7em;

      .close-button {
         position: absolute;
         right: 0.1em;
         top: 50%;
         padding: 0.5em;
         transform: translateY(-50%);
         cursor: pointer;
         width: 1.3em;
         fill: #929292;

         &:hover {
            fill: #3b3b3b;
         }
      }
   }
   .MuiAutocomplete-popper {
      box-shadow: 0 0.1em 0.5em rgba(0, 0, 0, 0.2);
      border-radius: 0.5em;
      overflow: hidden;
      & > * > * {
         width: 100%;
         max-height: 14em;
      }

      & > * > * > * {
         ${border.bottom};
      }
   }
`

export default AutoCompleteSelectField
